import { useId } from 'react'
import type { InputHTMLAttributes, JSX, ReactNode } from 'react'

/**
 * A labelled control with help and error text, wired for assistive technology.
 *
 * The wiring is the reason this exists rather than each form repeating the
 * markup: `aria-describedby` has to name the help text *and* the error, and
 * `aria-invalid` has to follow the error's presence. Getting that right six
 * times by hand is six chances to get it wrong once, invisibly.
 *
 * Validity is announced by border, icon and message together, never by colour
 * alone — the `data-state` attribute the Atlas layer keys off (S-D1).
 */
export function Field({
  label,
  error,
  help,
  required,
  children,
}: {
  label: string
  error?: string | undefined
  help?: string | undefined
  required?: boolean | undefined
  /** Receives the id and the aria wiring the label and messages depend on. */
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
    required: boolean | undefined
  }) => ReactNode
}): JSX.Element {
  const id = useId()
  const helpId = `${id}-help`
  const errorId = `${id}-error`
  const describedBy = [help ? helpId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className="ar-field" data-state={error ? 'invalid' : undefined}>
      <label className="ar-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="ar-req" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
        required,
      })}
      {help ? (
        <span className="ar-field__help" id={helpId}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="ar-field__error" id={errorId}>
          <span aria-hidden="true">!</span>
          {error}
        </span>
      ) : null}
    </div>
  )
}

/** The common case: a `Field` wrapping one `<input>`. */
export function TextField({
  label,
  error,
  help,
  ...input
}: {
  label: string
  error?: string | undefined
  help?: string | undefined
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'>): JSX.Element {
  return (
    <Field label={label} error={error} help={help} required={input.required}>
      {(aria) => <input className="ar-input" {...aria} {...input} />}
    </Field>
  )
}
